/* eslint-disable max-len */
import { CommonModule } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { AppConfigService } from '@core/appconfig.service';
import { ProductService } from '@core/product.service';
import { ReportsService } from '@core/reports.service';
import { QueryParamService } from '@core/services/query-param/query-param.service';
import { IObjective } from '@models/admin-benchmark';
import { Classroom } from '@models/classroom';
import { SchoolLicense } from '@models/license/school-license';
import { PagedResponse } from '@models/paged-response';
import { IPrintableReportHeading } from '@models/report-print-heading';
import { Topic } from '@models/topic';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import {
  BrandedFallbackDownloadComponent
} from '@shared/branded-fallback-download/branded-fallback-download.component';
import {
  BrandedClassOverTimeReportDetailComponent
} from '@shared/branded-reports/branded-class-over-time-report-detail/branded-class-over-time-report-detail.component';
import {
  ClassOverTimeReport,
  ClassOverTimeReportRequest
} from '@shared/branded-reports/branded-class-over-time-report-detail/class-over-time-report-detail.model';
import {
  BrandedClassSpreadReportDetailComponent
} from '@shared/branded-reports/branded-class-spread-report-detail/branded-class-spread-report-detail.component';
import {
  ClassSpreadBenchmarkResult,
  ClassSpreadReport,
  ClassSpreadReportRequest, StudentOverTimeReportRequest
} from '@shared/branded-reports/branded-class-spread-report-detail/class-spread-report-detail.model';
import {
  AssessmentStatus,
  StudentOverTimeReport
} from '@shared/branded-reports/branded-individual-student-over-time-report-detail/student-over-time-report-detail.model';
import {
  StandardsOverTimeReportDetailComponent
} from '@shared/branded-reports/standards-over-time-report-detail/standards-over-time-report-detail.component';
import { ButtonComponent } from '@shared/button/button.component';
import { NgSelectSrValueDirective } from '@shared/directives/ng-select-sr-value.directive';
import { Capabilities } from '@shared/enums/capabilities';
import { CapabilityType } from '@shared/enums/capability-type';
import {
  getReportTypeFromQueryParam,
  ProgressReportQueryParamByType,
  ProgressReportType,
  ProgressReportTypeModel
} from '@shared/enums/progress-report-type';
import { TopicType } from '@shared/enums/topic-type';
import { FormHelpers } from '@shared/form-helpers';
import { Helpers } from '@shared/helpers';
import { IPrintAllScoreDetailsParams } from '@shared/modals/score-detail-modal/score-detail-params';
import { ScoreDetailPrintAllModalComponent } from '@shared/modals/score-detail-print-all-modal/score-detail-print-all-modal.component';
import { SelectComponent } from '@shared/select/select.component';
import { ZbpDataTable } from '@shared/zbp-data-table/zbp-data-table.model';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import _, { isEqual } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  mergeMap,
  Observable,
  of,
  skipWhile,
  Subscription,
  tap
} from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { ClassroomService } from 'src/app/classrooms/classes/classroom/classroom.service';
import { LearningFacade } from 'src/app/classrooms/store/learning.facade';
import { AdminSkillsAverageDetailComponent } from '../branded-benchmark-reports/admin-skills-average-report/admin-skills-average-detail/admin-skills-average-detail.component';
import { BenchmarkSkillsAverageReport } from '../branded-benchmark-reports/admin-skills-average-report/skills-average-report-detail.model';
import { BenchmarkStandardsOverTimeReport } from '../branded-benchmark-reports/admin-standards-over-time-report/standards-over-time-report-detail.model';
import {
  BrandedClassSkillsHeatmapReportDetailComponent
} from '../branded-class-skills-heatmap-report-detail/branded-class-skills-heatmap-report-detail.component';
import {
  ClassSkillsHeatmapReport,
  ClassSkillsHeatmapReportRequest
} from '../branded-class-skills-heatmap-report-detail/class-skills-heatmap-report-detail.model';
import {
  BrandedIndividualStudentOverTimeReportDetailComponent
} from '../branded-individual-student-over-time-report-detail/branded-individual-student-over-time-report-detail.component';
import {
  BrandedReportNoSelectionComponent,
} from '../branded-report-no-selection/branded-report-no-selection.component';
import { BrandedReportsHelpers } from '../branded-reports-helpers';
import { BrandedReportsPrintHelpers } from '../branded-reports-print-helpers';
import { SharedStandardsDetailComponent } from '../shared-standards-detail/shared-standards-detail-report.component';
import { StandardsDetailReport } from '../shared-standards-detail/shared-standards-detail-report.model';
import PageChangeEvent = ZbpDataTable.PageChangeEvent;

/* eslint-enable max-len */

/**
 * Handles all controls and the displaying of report tables for all reports types in {@link ProgressReportType}
 * (Class Over Time, Individual Student Over Time, Class Spread, Class Skills Heatmap)
 *
 *
 * The report type selected from the report dropdown controls which other dropdowns are active.
 *
 * Displays the matching report component based off which report was ran.
 */
@Component({
  selector: 'zbp-branded-progress-reports',
  templateUrl: './branded-progress-reports.component.html',
  styleUrl: './branded-progress-reports.component.scss',
  imports: [
    NgSelectModule,
    NgSelectSrValueDirective,
    ReactiveFormsModule,
    ButtonComponent,
    BrandedReportNoSelectionComponent,
    CommonModule,
    BrandedClassOverTimeReportDetailComponent,
    BrandedIndividualStudentOverTimeReportDetailComponent,
    BrandedClassSpreadReportDetailComponent,
    BrandedClassSkillsHeatmapReportDetailComponent,
    BrandedFallbackDownloadComponent,
    NgbTooltip,
    SharedStandardsDetailComponent,
    SelectComponent,
    StandardsOverTimeReportDetailComponent,
    AdminSkillsAverageDetailComponent,
  ]
})
export class BrandedProgressReportsComponent implements OnInit, OnDestroy, AfterViewInit {

  protected readonly ProgressReportType = ProgressReportType;
  protected readonly iconNames = zbpIconNames;
  protected readonly ProgressReportQueryParamByType = ProgressReportQueryParamByType;

  protected readonly VIEW_WHOLE_CLASS_OPTION = {
    value: 'whole-class',
    label: 'Whole Class'
  } as const;

  protected readonly VIEW_TOTAL_SCORE_OPTION = {
    value: 'total-score',
    label: 'Total Score'
  } as const;

  protected readonly formControlNames = {
    studentId: 'studentId',
    report: 'report',
    classroomId: 'classroomId',
    productSkuId: 'productSkuId',
    activityId: 'activityId',
    grade: 'grade',
    skill: 'skill',
  };

  /** Configuration for Class Over Time Report */
  classOverTimeConfig: ProgressReportTypeModel = {
    type: ProgressReportType.ClassOverTime,
    queryParam: ProgressReportQueryParamByType.ClassOverTime,
    isPrinting: false,
    isExporting: false,
  };

  /** Configuration for Individual Student Over Time Report */
  individualStudentOverTimeConfig: ProgressReportTypeModel = {
    type: ProgressReportType.IndividualStudentOverTime,
    queryParam: ProgressReportQueryParamByType.IndividualStudentOverTime,
    isPrinting: false,
    isExporting: false,
  };

  /** Configuration for Class Spread Over Time Report */
  classSpreadTimeConfig: ProgressReportTypeModel = {
    type: ProgressReportType.ClassSpread,
    queryParam: ProgressReportQueryParamByType.ClassSpread,
    isPrinting: false,
    isExporting: false,
  };

  /** Configuration for Class Skills Heatmap Report */
  classSkillsHeatmapConfig: ProgressReportTypeModel = {
    type: ProgressReportType.ClassSkillsHeatmap,
    queryParam: ProgressReportQueryParamByType.ClassSkillsHeatmap,
    isPrinting: false,
    isExporting: false,
  };

  /** Configuration for Standards Detail Report */
  standardsDetailConfig: ProgressReportTypeModel = {
    type: ProgressReportType.StandardsDetail,
    queryParam: ProgressReportQueryParamByType.StandardsDetail,
    isPrinting: false,
    isExporting: false,
  };

  /** Configuration for Standards Over Time Report */
  standardsOverTimeConfig: ProgressReportTypeModel = {
    type: ProgressReportType.StandardsOverTime,
    queryParam: ProgressReportQueryParamByType.StandardsOverTime,
    isPrinting: false,
    isExporting: false,
  };

  /** Configuration for Skill Average Report */
  skillAveragesConfig: ProgressReportTypeModel = {
    type: ProgressReportType.SkillAverages,
    queryParam: ProgressReportQueryParamByType.SkillAverages,
    isPrinting: false,
    isExporting: false,
  };

  /**
   * List of configurations for all report types
   *
   * Other report types are dynamically added based on user's state
   */
  readonly reportTypes: ProgressReportTypeModel[] = [
    this.classOverTimeConfig,
    this.individualStudentOverTimeConfig,
    this.classSpreadTimeConfig,
  ];

  readonly defaultControlNames = [
    this.formControlNames.report,
    this.formControlNames.classroomId,
    this.formControlNames.productSkuId
  ];
  readonly reportControl: FormControl<ProgressReportQueryParamByType> = new FormControl(
    this.classOverTimeConfig.queryParam,
    [
      Validators.required,
      FormHelpers.getOneOfValidator(Object.values(ProgressReportQueryParamByType)),
    ]
  );

  readonly reportTypesWithOnPageHelp: Record<string, string> = {
    [ProgressReportQueryParamByType.ClassOverTime]: ProgressReportQueryParamByType.ClassOverTime,
    [ProgressReportQueryParamByType.IndividualStudentOverTime]:
      ProgressReportQueryParamByType.IndividualStudentOverTime,
    [ProgressReportQueryParamByType.ClassSpread]: ProgressReportQueryParamByType.ClassSpread,
    [ProgressReportQueryParamByType.ClassSkillsHeatmap]: ProgressReportQueryParamByType.ClassSkillsHeatmap,
  };

  // Static Form Controls for all Over Time reports
  readonly classroomIdControl: FormControl<string> = new FormControl(null, Validators.required);
  selectedClassroom?: Classroom;
  readonly productSkuIdControl: FormControl<string> = new FormControl(null, Validators.required);
  /** updated by productSkuIdControl */
  selectedProduct?: SchoolLicense;

  // Dynamic Controls
  readonly studentIdControl: FormControl<string> = new FormControl(null, Validators.required);
  readonly activityIdControl: FormControl<string> = new FormControl(null, Validators.required);

  readonly viewGroupControl = new FormControl<string>(this.VIEW_WHOLE_CLASS_OPTION.value);

  readonly skillControl: FormControl<string> = new FormControl(null, Validators.required);

  readonly objectiveFilterControl = new FormControl<string>(this.VIEW_TOTAL_SCORE_OPTION.value);

  readonly heatmapReportType = -1;

  /** Headers that are sent to the header component. Varies based on report type selected. */
  printableReportHeadings: IPrintableReportHeading[] = [];
  /** Used to show print button */
  enablePrint: boolean = false;
  /** Used to show spinner for Print button */
  isPrinting: boolean = false;

  /** Used to show Print All Score Details button */
  showPrintAllScoreDetails: boolean = false;
  /** Used to enable Print All Score Details button */
  enablePrintAllScoreDetails: boolean = false;
  disabledPrintAllScoreDetailsTooltip: string = 'Printing is disabled because no scores exist.';

  /** Used for export button and spinner */
  enableExport: boolean = false;
  isExporting: boolean = false;

  classrooms: Classroom[] = [];
  products: SchoolLicense[] = [];
  products$: BehaviorSubject<SchoolLicense[]> = new BehaviorSubject([]);
  isLoadingClassrooms: boolean = false;
  isLoadingProducts: boolean = false;
  isLoadingSkills: boolean = false;
  isProductControlSetByQueryParam = false;
  isStudentControlSetByQueryParam = false;
  isActivityControlSetByQueryParam = false;
  defaultDropDownText: string = 'Make Selection';
  statusMessage: string = '';
  page = 1;
  pageSize = 25;
  maxPageCount: number;
  collectionSize: number;

  /** Used by all reports to ensure auto-search is only performed once on page load */
  private hasDoneInitialSearchFromQueryParams = false;
  /** Flag to ensure the products subscription is only created once */
  private productsSubscriptionCreated = false;

  /**
   * The data selection form has controls dynamically added based on the report selection.
   *
   * All progress report forms share:
   *    - Report
   *    - Classroom ID
   *    - Product SKU ID
   */
  selectionForm: FormGroup = this.formBuilder.group({
    [this.formControlNames.report]: this.reportControl,
    [this.formControlNames.classroomId]: this.classroomIdControl,
    [this.formControlNames.productSkuId]: this.productSkuIdControl,
  });

  // Student Dropdown fields
  showStudentDropdown = false;
  isLoadingStudent = false;
  students = [];

  // Grade Dropdown field instead of product
  showGradeDropdown = false;

  showSkillDropdown = false;

  // Activity Dropdown Fields
  showActivityDropdown = false;
  isLoadingActivity = false;
  topicsList$: BehaviorSubject<Topic[]> = new BehaviorSubject([]);
  activityReportTypes = [ProgressReportQueryParamByType.ClassSpread, ProgressReportQueryParamByType.ClassSkillsHeatmap];

  // Report Results that may be empty
  isLoadingReport: boolean = false;
  classOverTimeReportData?: ClassOverTimeReport;
  individualStudentOverTimeReportData?: StudentOverTimeReport;
  classSpreadReportData?: ClassSpreadReport;
  classSkillsHeatmapReportData?: ClassSkillsHeatmapReport;
  classStandardsDetailReportData?: PagedResponse<StandardsDetailReport[]>;
  standardsOverTimeReportData?: BenchmarkStandardsOverTimeReport;
  skillAveragesReportData?: BenchmarkSkillsAverageReport;

  // View Group Dropdown Fields
  showViewGroupDropdown = false;
  viewGroupOptions: { value: string, label: string }[] = [this.VIEW_WHOLE_CLASS_OPTION];

  // Objective Filter
  showObjectiveFilter = false;
  objectiveFilterInitialized = false;
  objectiveFilterOptions: { value: string, label: string }[] = [this.VIEW_TOTAL_SCORE_OPTION];

  @ViewChild('exportDownload', { static: false }) exportDownload: BrandedFallbackDownloadComponent;

  private readonly subscriptions = new Subscription();
  private readonly _learningFacadeClassrooms$ = new BehaviorSubject<Classroom[]>(null);

  skills$: Observable<IObjective[]> = this.productSkuIdControl.valueChanges.pipe(
    tap((product) => {
      // reset skill control if no grade is selected
      if (!product) {
        this.skillControl.setValue(null);
        this.skillControl.disable();
      } else {
        // enable in case we skip on distinctUntilChanged
        this.skillControl.enable();
      }
    }),
    // Only proceed when all required values are present and products are loaded
    filter(product => (!!product)),
    // Prevent duplicate API calls with the same combination of values
    distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
    switchMap((product: string) => {
      this.isLoadingSkills = true;
      this.skillControl.disable();
      const selectedGrade = this.products.find(value => value.skuId === product);
      if (!selectedGrade) {
        return of(null);
      }
      return this.reportsService.getObjectivesByProductVariant(selectedGrade.productType, selectedGrade.variant);
    }),
    skipWhile(res => !res),
    map((res) => {
      this.isLoadingSkills = false;
      this.skillControl.enable();

      if (res?.response) {
        // Check if the existing skill is part of the new product
        if (!(res.response.find(x => x.objective === this.skillControl.value))) {
          this.skillControl.setValue(null);
        }

        return [...res.response];
      }

      this.skillControl.setValue(null);
      return [];
    }),
    shareReplay(),
  );

  constructor(
    private formBuilder: FormBuilder,
    private queryParamService: QueryParamService,
    private classroomService: ClassroomService,
    private learningFacade: LearningFacade,
    private productService: ProductService,
    private reportsService: ReportsService,
    private toastr: ToastrService,
    private appConfigService: AppConfigService,
    private titleService: Title,
    private modalService: NgbModal,
  ) {
    this.subscriptions.add(this.learningFacade.classrooms$.subscribe(this._learningFacadeClassrooms$));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.productsSubscriptionCreated = false;
    window.removeEventListener('afterprint', this.afterPrint);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.learningFacade.productLines$.pipe(
        tap((productLines) => {
          const hasHeatmapReportCapability = productLines
            .map(productLine => productLine.variants)
            .flat()
            // eslint-disable-next-line max-len
            .some(variant => Helpers.hasProductVariantCapability(variant, Capabilities.HeatmapReport, CapabilityType.Classroom));
          if (hasHeatmapReportCapability) {
            this.reportTypes.push(this.classSkillsHeatmapConfig);
          }

          const hasStandardsDetailReportCapability = productLines
            .map(productLine => productLine.variants)
            .flat()
            // eslint-disable-next-line max-len
            .some(variant => Helpers.hasProductVariantCapability(variant, Capabilities.StandardsDetailReport, CapabilityType.Classroom));
          if (hasStandardsDetailReportCapability) {
            this.reportTypes.push(this.standardsDetailConfig);
          }

          const hasStandardsOverTimeReportCapability = productLines
            .map(productLine => productLine.variants)
            .flat()
            // eslint-disable-next-line max-len
            .some(variant => Helpers.hasProductVariantCapability(variant, Capabilities.StandardsOverTimeReport, CapabilityType.Classroom));
          if (hasStandardsOverTimeReportCapability) {
            this.reportTypes.push(this.standardsOverTimeConfig);
          }

          const hasSkillAveragesReportCapability = productLines
            .map(productLine => productLine.variants)
            .flat()
            // eslint-disable-next-line max-len
            .some(variant => Helpers.hasProductVariantCapability(variant, Capabilities.SkillsAverageReport, CapabilityType.Classroom));
          if (hasSkillAveragesReportCapability) {
            this.reportTypes.push(this.skillAveragesConfig);
          }
        })
      ).subscribe()
    );
    this.initDefaultControlSubscriptions();
    this.initDynamicFieldsByReportType();
    this.initQueryParamBindings();

    // If query params did not set a report, default to Class Over Time
    if (!this.reportControl.value) {
      this.reportControl.setValue(this.classOverTimeConfig.queryParam);
    }
  }

  ngAfterViewInit(): void {
    // After printing is completed/cancelled,
    // remove spinner from print button and reset `isPrinting` for all report types
    // This needs to be added and removed manually because afterprint also exists on score detail modal
    window.addEventListener('afterprint', this.afterPrint);
  }

  // This needs to be a property to keep context
  afterPrint = () => {
    this.isPrinting = false;
    this.enablePrint = true;
    this.resetIsPrintingForReportConfigs();
  };

  onRefresh(): void {
    this.resetReports();
    this.onApplyButtonClick(false);
  }

  resetReports(): void {
    this.classOverTimeReportData = undefined;
    this.individualStudentOverTimeReportData = undefined;
    this.classSpreadReportData = undefined;
    this.classSkillsHeatmapReportData = undefined;
    this.classStandardsDetailReportData = undefined;
    this.standardsOverTimeReportData = undefined;
    this.skillAveragesReportData = undefined;
  }

  onApplyButtonClick(srAnnounce: boolean = true): void {
    this.enablePrint = false;
    this.enableExport = false;
    this.showPrintAllScoreDetails = false;
    this.page = 1;
    this.collectionSize = 0;
    this.maxPageCount = 0;
    if (this.exportDownload) {
      this.exportDownload.reset();
    }
    this.resetPrintableReportHeadings();

    if (this.selectionForm.valid) {
      switch (this.reportControl.value) {
      case ProgressReportQueryParamByType.ClassOverTime:
        this.loadClassOverTimeReport(true, srAnnounce);
        break;
      case ProgressReportQueryParamByType.IndividualStudentOverTime:
        this.loadIndividualStudentOverTimeReport(srAnnounce);
        break;
      case ProgressReportQueryParamByType.ClassSpread:
        this.loadClassSpreadReport(srAnnounce);
        break;
      case ProgressReportQueryParamByType.ClassSkillsHeatmap:
        this.loadClassSkillsHeatmapReport(srAnnounce);
        break;
      case ProgressReportQueryParamByType.StandardsDetail:
        this.loadStandardsDetailReport(srAnnounce);
        break;
      case ProgressReportQueryParamByType.StandardsOverTime:
        this.loadStandardsOverTimeReport(srAnnounce);
        break;
      case ProgressReportQueryParamByType.SkillAverages:
        this.loadSkillAveragesReport(srAnnounce);
        break;
      default:
        // unsupported report type
        break;
      }
    }
  }

  loadClassOverTimeReport(resetFilters = true, srAnnounce = false) {
    this.classOverTimeReportData = undefined;
    const product = this.products.find(
      p => p.skuId === this.productSkuIdControl.value
    );
    const params: ClassOverTimeReportRequest = {
      productType: product.productType,
      grade: product.variantType,
    };
    this.conditionallyAppendViewGroupParam(params);
    this.conditionallyAppendObjectiveFilter(params);
    this.isLoadingReport = true;
    this.subscriptions.add(
      this.reportsService.getClassOverTimeReport(
        this.classroomIdControl.value,
        params
      ).pipe(
        map((res) => {
          if (res.success && res.response.benchmarkResults.length > 0) {
            if (srAnnounce) {
              this.announceResultsStatus(res.response.benchmarkResults.length);
            }
            return res.response;
          }
          if (res.response && res.response.classroomStudentTotalCount === 0) {
            throw new Error('There are no students in the class');
          }
          throw new Error(res?.messages?.[0] ?? 'Unable to load class over time report');
        }),
        finalize(() => {
          this.isLoadingReport = false;
        }),
      ).subscribe({
        next: (report) => {
          this.classOverTimeReportData = report;
          this.configureButtonStates();
          if (resetFilters) {
            this.loadObjectiveOptions(report);
          }
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
        }
      })
    );
  }

  loadIndividualStudentOverTimeReport(srAnnounce = false) {
    this.individualStudentOverTimeReportData = undefined;
    const product = this.products.find(
      p => p.skuId === this.productSkuIdControl.value
    );
    const params = {
      productType: product.productType,
      grade: product.variantType,
      userId: this.studentIdControl.value,
    };
    this.isLoadingReport = true;
    this.subscriptions.add(
      this.reportsService.getStudentOverTimeReport(
        this.classroomIdControl.value,
        params
      ).pipe(
        map((res) => {
          if (res.success && res.response.benchmarkResults.length > 0) {
            if (srAnnounce) {
              this.announceResultsStatus(res.response.benchmarkResults.length);
            }
            return res.response;
          }
          throw new Error(res?.messages?.[0] ?? 'Unable to load individual student over time report');
        }),
        finalize(() => {
          this.isLoadingReport = false;
        }),
      ).subscribe({
        next: (report) => {
          this.individualStudentOverTimeReportData = report;
          this.configureButtonStates();
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
        }
      })
    );
  }

  loadClassSpreadReport(srAnnounce = false) {
    this.classSpreadReportData = undefined;
    const product = this.products.find(
      p => p.skuId === this.productSkuIdControl.value
    );
    const params: ClassSpreadReportRequest = {
      productType: product.productType,
      grade: product.variantType,
      assessmentKey: this.activityIdControl.value,
    };
    this.conditionallyAppendViewGroupParam(params);
    this.isLoadingReport = true;
    this.subscriptions.add(
      this.reportsService.getClassSpreadReport(
        this.classroomIdControl.value,
        params
      ).pipe(
        map((res) => {
          if (res.success && res.response.benchmarkResults.length > 0) {
            if (srAnnounce) {
              this.announceResultsStatus(res.response.benchmarkResults.length);
            }
            return res.response;
          }
          if (res.response && res.response.classroomStudentTotalCount === 0) {
            throw new Error('There are no students in the class');
          }
          throw new Error(res?.messages?.[0] ?? 'Unable to load class spread report');
        }),
        finalize(() => {
          this.isLoadingReport = false;
        }),
      ).subscribe({
        next: (report) => {
          this.classSpreadReportData = report;
          this.configureButtonStates();
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
        }
      })
    );
  }

  loadClassSkillsHeatmapReport(srAnnounce = false) {
    this.classSkillsHeatmapReportData = undefined;
    const product = this.products.find(
      p => p.skuId === this.productSkuIdControl.value
    );
    const params: ClassSkillsHeatmapReportRequest = {
      productType: product.productType,
      grade: product.variantType,
      assessmentKey: this.activityIdControl.value,
    };
    this.conditionallyAppendViewGroupParam(params);
    this.isLoadingReport = true;
    this.subscriptions.add(
      this.reportsService.getClassSkillsHeatmapReport(
        this.classroomIdControl.value,
        params
      ).pipe(
        map((res) => {
          if (res.success && res.response) {
            if (srAnnounce) {
              this.announceResultsStatus(this.heatmapReportType);
            }
            return res.response;
          }
          throw new Error(res?.messages?.[0] ?? 'Unable to load class skills heatmap report');
        }),
        finalize(() => {
          this.isLoadingReport = false;
        }),
      ).subscribe({
        next: (report) => {
          this.classSkillsHeatmapReportData = report;
          this.configureButtonStates();
        },
        error: (err: Error) => {
          this.toastr.error(err.message);
        }
      })
    );
  }

  loadStandardsDetailReport(srAnnounce = false) {
    const product = this.products.find(
      p => p.skuId === this.productSkuIdControl.value
    );
    const params = {
      classroomId: this.classroomIdControl.value,
      productType: product.productType,
      grade: product.variantType,
      pageNumber: this.page,
      pageSize: this.pageSize,
    };
    this.isLoadingReport = true;

    if (!product) {
      this.isLoadingReport = false;
      this.toastr.error('Invalid product selected');
    } else {
      this.subscriptions.add(
        this.reportsService.getClassStandardDetailReport(
          params
        ).pipe(
          map((res) => {
            if (res.success && res.response) {
              if (srAnnounce) {
                this.announceResultsStatus(res.totalRecords);
              }
              return res;
            }
            throw new Error(res?.messages?.[0] ?? 'Unable to load standards detail report');
          }),
          finalize(() => {
            this.isLoadingReport = false;
          }),
        ).subscribe({
          next: (report) => {
            this.classStandardsDetailReportData = report;
            this.configureButtonStates();
          },
          error: (err: Error) => {
            this.toastr.error(err.message);
          }
        })
      );
    }
  }

  loadSkillAveragesReport(srAnnounce = false) {
    const product = this.products.find(
      p => p.skuId === this.productSkuIdControl.value
    );
    const schoolId = this.classrooms
      .find(classroom => classroom.classroomId === this.classroomIdControl.value).educationalUnitId;
    const params = {
      schoolId,
      classroomId: this.classroomIdControl.value,
      productType: product.productType,
      grade: product.variantType,
      objective: this.skillControl.value,
      pageNumber: this.page,
      pageSize: this.pageSize,
    };
    this.isLoadingReport = true;

    if (!product) {
      this.isLoadingReport = false;
      this.toastr.error('Invalid product selected');
    } else {
      this.subscriptions.add(
        this.reportsService.getBenchmarkSkillsAverageReport(
          params
        ).pipe(
          map((res) => {
            if (res.success && res.response) {
              if (srAnnounce) {
                this.announceResultsStatus(res.totalRecords);
              }
              return res;
            }
            throw new Error(res?.messages?.[0] ?? 'Unable to load skill averages report');
          }),
          finalize(() => {
            this.isLoadingReport = false;
          }),
        ).subscribe({
          next: (report) => {
            this.maxPageCount = report.totalPages;
            this.collectionSize = report.totalRecords;
            [this.skillAveragesReportData] = report.response;
            this.configureButtonStates();
          },
          error: (err: Error) => {
            this.toastr.error(err.message);
          }
        })
      );
    }
  }

  loadStandardsOverTimeReport(srAnnounce = false) {
    const product = this.products.find(
      p => p.skuId === this.productSkuIdControl.value
    );
    const params = {
      classroomId: this.classroomIdControl.value,
      productType: product.productType,
      grade: product.variantType,
      objective: this.skillControl.value,
    };
    this.isLoadingReport = true;

    if (!product) {
      this.isLoadingReport = false;
      this.toastr.error('Invalid product selected');
    } else {
      this.subscriptions.add(
        this.reportsService.getClassStandardsOverTimeReport(
          params
        ).pipe(
          map((res) => {
            if (res.success && res.response) {
              if (srAnnounce) {
                this.announceResultsStatus(res.response.benchmarkResults.length);
              }
              return res.response;
            }
            throw new Error(res?.messages?.[0] ?? 'Unable to load standards over time report');
          }),
          finalize(() => {
            this.isLoadingReport = false;
          }),
        ).subscribe({
          next: (report) => {
            this.standardsOverTimeReportData = report;
            this.configureButtonStates();
          },
          error: (err: Error) => {
            this.toastr.error(err.message);
          }
        })
      );
    }
  }

  standardsDetailPageChange(event: PageChangeEvent): void {
    this.page = event.page;
    this.loadStandardsDetailReport(true);
  }

  skillPageChange(event: PageChangeEvent): void {
    this.page = event.page;
    this.loadSkillAveragesReport(true);
  }

  applyClassOverTimeSelections() {
    // Trigger the shared products subscription to handle Class Over Time logic
    this.handleProductsSubscriptionLogic();
  }

  /**
   * Used by QueryParamService to trigger report load when a student is selected by params
   */
  applyStudentOverTimeSelections() {
    // Trigger the shared products subscription to handle Student Over Time logic
    this.handleProductsSubscriptionLogic();
  }

  /**
   * Printing is triggered through {@link BrandedProgressReportsComponent}
   */
  print() {
    const currentReport = this.reportControl.value;

    this.resetPrinting();
    this.isPrinting = true;

    /**
     * The `isPrinting` flag on the {@link ProgressReportQueryParamByType} for the selected report
     * can be used to trigger printing in the report component
     */
    switch (currentReport) {
    case ProgressReportQueryParamByType.ClassOverTime:
      this.getClassOverTimePrintHeaders();
      this.classOverTimeConfig.isPrinting = true;
      break;
    case ProgressReportQueryParamByType.ClassSkillsHeatmap:
      this.getClassSkillsHeatmapPrintHeaders();
      this.classSkillsHeatmapConfig.isPrinting = true;
      break;
    case ProgressReportQueryParamByType.ClassSpread:
      this.getClassSpreadPrintHeaders();
      this.classSpreadTimeConfig.isPrinting = true;
      break;
    case ProgressReportQueryParamByType.IndividualStudentOverTime:
      this.getIndividualStudentPrintHeaders();
      this.individualStudentOverTimeConfig.isPrinting = true;
      break;
    default:
      this.resetPrinting();
    }
  }

  export() {
    this.resetExporting();
    const currentReport = this.reportControl.value;
    this.isExporting = true;
    this.enableExport = true;

    switch (currentReport) {
    case ProgressReportQueryParamByType.ClassSkillsHeatmap:
      this.exportClassSkillsHeatmap();
      break;
    case ProgressReportQueryParamByType.ClassOverTime:
      this.exportClassOverTimeReport();
      break;
    case ProgressReportQueryParamByType.ClassSpread:
      this.exportClassSpreadReport();
      break;
    case ProgressReportQueryParamByType.IndividualStudentOverTime:
      this.exportStudentOverTimeReport();
      break;
    default:
      this.resetExporting();
    }
  }

  private exportClassSkillsHeatmap() {
    this.classSkillsHeatmapConfig.isExporting = true;
    const product = this.findProductById(this.productSkuIdControl.value);
    const viewBy = this.viewGroupControl.value;

    const params = {
      productType: product.productType,
      grade: product.variantType,
      assessmentKey: this.activityIdControl.value,
    };

    if (!(viewBy === this.VIEW_WHOLE_CLASS_OPTION.value)) {
      params['classroomStudentGroupId'] = viewBy;
    }

    this.subscriptions.add(
      this.reportsService.exportClassSkillsHeatmapReport(this.classroomIdControl.value, params)
        .pipe(
          mergeMap((response?: HttpResponse<Blob>) => Helpers.downloadFile(
            this.exportDownload,
            'class-skills-heatmap-report',
            response.body,
            'xlsx'
          )),
          finalize(() => {
            this.isExporting = false;
          })
        )
        .subscribe((success) => {
          if (!success) {
            console.log('error during export class over time', success);
            this.toastr.error('An error occurred exporting this report.');
          }
        }),
    );
  }

  private exportClassOverTimeReport() {
    this.classOverTimeConfig.isExporting = true;
    const product = this.findProductById(this.productSkuIdControl.value);

    const params: ClassOverTimeReportRequest = {
      productType: product.productType,
      grade: product.variantType,
    };
    this.conditionallyAppendViewGroupParam(params);
    this.conditionallyAppendObjectiveFilter(params);
    this.reportsService.exportClassOverTimeReport(this.classroomIdControl.value, params)
      .pipe(
        mergeMap((response?: HttpResponse<Blob>) => Helpers.downloadFile(
          this.exportDownload,
          'class-over-time-report',
          response.body,
          'xlsx'
        )),
        finalize(() => {
          this.isExporting = false;
        })
      )
      .subscribe((success) => {
        if (!success) {
          console.log('error during export class over time', success);
          this.toastr.error('An error occurred exporting this report.');
        }
      });

  }

  private exportClassSpreadReport() {
    this.classSpreadTimeConfig.isExporting = true;
    const product = this.products.find(
      p => p.skuId === this.productSkuIdControl.value
    );
    const params: ClassSpreadReportRequest = {
      productType: product.productType,
      grade: product.variantType,
      assessmentKey: this.activityIdControl.value,
    };
    this.conditionallyAppendViewGroupParam(params);
    this.reportsService.exportClassSpreadReport(this.classroomIdControl.value, params)
      .pipe(
        mergeMap((response?: HttpResponse<Blob>) => Helpers.downloadFile(
          this.exportDownload,
          'class-spread-report',
          response.body,
          'xlsx'
        )),
        finalize(() => {
          this.isExporting = false;
        })
      )
      .subscribe((success) => {
        if (!success) {
          console.log('error during export class spread', success);
          this.toastr.error('An error occurred exporting this report.');
        }
      });
  }

  /** Sets header data for Class Spread report on print. */
  private getClassSpreadPrintHeaders() {
    const reportSelected = this.reportControl.value;
    const matchingReport = this.findReportModelByType(reportSelected);

    this.printableReportHeadings = BrandedReportsPrintHelpers.getClassSkillsHeatmapAndSpreadPrintHeaders(
      matchingReport?.type,
      this.classroomIdControl,
      this.classrooms,
      this.productSkuIdControl,
      this.products,
      this.activityIdControl,
      this.topicsList$.value,
    );
  }

  /** Sets header data for Class Over Time report on print. */
  private getClassOverTimePrintHeaders() {
    const reportSelected = this.reportControl.value;
    const matchingReport = this.findReportModelByType(reportSelected);

    this.printableReportHeadings = BrandedReportsPrintHelpers.getClassOverTimePrintHeaders(
      matchingReport?.type,
      this.classroomIdControl,
      this.classrooms,
      this.productSkuIdControl,
      this.products,
    );
  }

  /** Sets header data for Class Skills Heatmap report on print. */
  private getClassSkillsHeatmapPrintHeaders() {
    const reportSelected = this.reportControl.value;
    const matchingReport = this.findReportModelByType(reportSelected);

    this.printableReportHeadings = BrandedReportsPrintHelpers.getClassSkillsHeatmapAndSpreadPrintHeaders(
      matchingReport?.type,
      this.classroomIdControl,
      this.classrooms,
      this.productSkuIdControl,
      this.products,
      this.activityIdControl,
      this.topicsList$.value,
    );
  }

  private exportStudentOverTimeReport() {
    this.individualStudentOverTimeConfig.isExporting = true;
    const product = this.products.find(
      p => p.skuId === this.productSkuIdControl.value
    );
    const params: StudentOverTimeReportRequest = {
      classroomId: this.classroomIdControl.value,
      productType: product.productType,
      grade: product.variantType,
      userId: this.studentIdControl.value,
    };
    this.reportsService.exportStudentOverTimeReport(params)
      .pipe(
        mergeMap((response?: HttpResponse<Blob>) => Helpers.downloadFile(
          this.exportDownload,
          'student-over-time-report',
          response.body,
          'xlsx'
        )),
        finalize(() => {
          this.isExporting = false;
        })
      )
      .subscribe((success) => {
        if (!success) {
          console.log('error during export student over time', success);
          this.toastr.error('An error occurred exporting this report.');
        }
      });
  }

  /** Sets header data for Individual Student Over Time report on print. */
  private getIndividualStudentPrintHeaders() {
    const reportSelected = this.reportControl.value;
    const matchingReport = this.findReportModelByType(reportSelected);

    this.printableReportHeadings = BrandedReportsPrintHelpers.getIndividualStudentPrintHeaders(
      matchingReport?.type,
      this.classroomIdControl,
      this.classrooms,
      this.productSkuIdControl,
      this.products,
      this.findStudentNameById(this.studentIdControl.value),
    );
  }

  /** Resets the print headings that are sent to the Print Heading component */
  private resetPrintableReportHeadings(): void {
    this.printableReportHeadings = BrandedReportsHelpers.resetPrintableReportHeadings(this.printableReportHeadings);
  }

  private configureButtonStates() {
    this.configurePrintButton();
    this.configureExportButton();
    this.configurePrintAllScoreDetailsButton();
  }

  /**
   * Updates `enablePrint` property based on report data being present for the selected report type.
   *
   * `enablePrint` is used on the template to enable the print button.
   */
  private configurePrintButton(): void {
    const reportSelected = this.reportControl.value;

    switch (reportSelected) {
    case ProgressReportQueryParamByType.ClassOverTime:
      this.enablePrint = !!this.classOverTimeReportData;
      break;
    case ProgressReportQueryParamByType.IndividualStudentOverTime:
      this.enablePrint = !!this.individualStudentOverTimeReportData;
      break;
    case ProgressReportQueryParamByType.ClassSkillsHeatmap:
      this.enablePrint = !!this.classSkillsHeatmapReportData;
      break;
    case ProgressReportQueryParamByType.ClassSpread:
      this.enablePrint = !!this.classSpreadReportData;
      break;
    default:
      this.enablePrint = false;
      break;
    }
  }

  /**
   * Updates `enableExport` property based on report data being present for the selected report type.
   *
   * `enableExport` is used on the template to enable the export button.
   */
  private configureExportButton(): void {
    const reportSelected = this.reportControl.value;
    if (this.exportDownload) {
      this.exportDownload.reset();
    }
    switch (reportSelected) {
    case ProgressReportQueryParamByType.ClassOverTime:
      this.enableExport = !!this.classOverTimeReportData;
      break;
    case ProgressReportQueryParamByType.ClassSpread:
      this.enableExport = !!this.classSpreadReportData;
      break;
    case ProgressReportQueryParamByType.ClassSkillsHeatmap:
      this.enableExport = !!this.classSkillsHeatmapReportData;
      break;
    case ProgressReportQueryParamByType.IndividualStudentOverTime:
      this.enableExport = !!this.individualStudentOverTimeReportData;
      break;
    default:
      this.enableExport = false;
      break;
    }
  }

  /**
   * Updates `enablePrintAllScoreDetails` property based on report data being present for the selected report type.
   *
   * `enablePrintAllScoreDetails` is used on the template to enable the print all score details button.
   *
   * Not all reports have this button.
   */
  private configurePrintAllScoreDetailsButton(): void {
    const reportSelected = this.reportControl.value;

    this.showPrintAllScoreDetails = false;
    this.enablePrintAllScoreDetails = false;

    switch (reportSelected) {
    case ProgressReportQueryParamByType.ClassSpread:
      this.showPrintAllScoreDetails = !!this.classSpreadReportData;
      this.enablePrintAllScoreDetails = !!this.classSpreadHasScoreDetails;
      break;
    default:
      break;
    }
  }

  private loadObjectiveOptions(report: ClassOverTimeReport): void {
    this.resetFilterOptions();
    this.objectiveFilterInitialized = true;
    if (report.objectives.length) {
      report.objectives.forEach((objective) => {
        this.objectiveFilterOptions.push({ value: objective, label: objective, });
      });
    }
  }

  applyFilter(): void {
    this.loadClassOverTimeReport(false, true);
  }

  /**
   * Single subscription handler that both applyClassOverTimeSelections and applyStudentOverTimeSelections react to
   */
  private handleProductsSubscriptionLogic() {
    // Only create the subscription once
    if (!this.productsSubscriptionCreated) {
      this.productsSubscriptionCreated = true;
      this.subscriptions.add(
        this.products$.pipe(
          filter(products => products.length > 0),
          map(() => {
            this.processClassOverTimeSelections();
            this.processStudentOverTimeSelections();
            this.processStandardsDetailSelections();
            this.processStandardsOverTimeSelections();
            this.processSkillAveragesSelections();
          })
        ).subscribe()
      );
    }
  }

  private processClassOverTimeSelections() {
    /** Should set up for auto-search if query params are setting form controls and report type is Class Over Time */
    const shouldSetupForSearch = !this.isProductControlSetByQueryParam
      && this.reportControl.value === this.classOverTimeConfig.queryParam
      && !this.hasDoneInitialSearchFromQueryParams;

    if (shouldSetupForSearch) {
      // Only perform the auto-search if all fields are set when setting from query params.
      if (this.selectionForm.valid && this.productSkuIdControl.value) {
        this.isProductControlSetByQueryParam = true;
        this.onApplyButtonClick(false);
      }
      this.hasDoneInitialSearchFromQueryParams = true;
    }
  }

  private processStudentOverTimeSelections() {
    /** Should set up for auto-search if query params are setting form controls
     * and report type is Individual Student Over Time */
    const shouldSetupForSearch = !this.isStudentControlSetByQueryParam
      && this.reportControl.value === this.individualStudentOverTimeConfig.queryParam
      && !this.hasDoneInitialSearchFromQueryParams;

    if (shouldSetupForSearch) {
      // Only perform the auto-search if all fields are set when setting from query params.
      if (this.selectionForm.valid && this.studentIdControl.value) {
        this.isStudentControlSetByQueryParam = true;
        this.onApplyButtonClick(false);
      }
      this.hasDoneInitialSearchFromQueryParams = true;
    }
  }

  private processStandardsDetailSelections() {
    /** Should set up for auto-search if query params are setting form controls
     * and report type is Standards Detail */
    const shouldSetupForSearch = !this.isProductControlSetByQueryParam
      && this.reportControl.value === this.standardsDetailConfig.queryParam
      && !this.hasDoneInitialSearchFromQueryParams;

    if (shouldSetupForSearch) {
      // Only perform the auto-search if all fields are set when setting from query params.
      if (this.selectionForm.valid && this.productSkuIdControl.value) {
        this.isProductControlSetByQueryParam = true;
        this.onApplyButtonClick(false);
      }
      this.hasDoneInitialSearchFromQueryParams = true;
    }
  }

  private processStandardsOverTimeSelections() {
    /** Should set up for auto-search if query params are setting form controls
     * and report type is Standards Over Time */
    const shouldSetupForSearch = !this.isProductControlSetByQueryParam
      && this.reportControl.value === this.standardsOverTimeConfig.queryParam
      && !this.hasDoneInitialSearchFromQueryParams;

    if (shouldSetupForSearch) {
      // Only perform the auto-search if all fields are set when setting from query params.
      if (this.selectionForm.valid && this.productSkuIdControl.value && this.skillControl.value) {
        this.isProductControlSetByQueryParam = true;
        this.onApplyButtonClick(false);
      }
      this.hasDoneInitialSearchFromQueryParams = true;
    }
  }

  private processSkillAveragesSelections() {
    /** Should set up for auto-search if query params are setting form controls
     * and report type is Skill Averages */
    const shouldSetupForSearch = !this.isProductControlSetByQueryParam
      && this.reportControl.value === this.skillAveragesConfig.queryParam
      && !this.hasDoneInitialSearchFromQueryParams;

    if (shouldSetupForSearch) {
      // Only perform the auto-search if all fields are set when setting from query params.
      if (this.selectionForm.valid && this.productSkuIdControl.value && this.skillControl.value) {
        this.isProductControlSetByQueryParam = true;
        this.onApplyButtonClick(false);
      }
      this.hasDoneInitialSearchFromQueryParams = true;
    }
  }

  applyClassSpreadSelections() {
    let reportConfiguredByQueryParams = false;

    this.subscriptions.add(
      // Topics list is the last data retrieved for Class Spread report
      this.topicsList$.pipe(
        filter(topics => topics.length > 0)
      ).subscribe(() => {
        const shouldSetupForSearch = !this.isActivityControlSetByQueryParam
          && (this.reportControl.value === this.classSpreadTimeConfig.queryParam
            || this.reportControl.value === this.classSkillsHeatmapConfig.queryParam)
          && !this.hasDoneInitialSearchFromQueryParams
          && !reportConfiguredByQueryParams;

        /** Should set up for auto-search if query params are setting form controls and report type is Class Spread */
        if (shouldSetupForSearch) {
          // Only perform the auto-search if all fields are set when setting from query params.
          if (this.selectionForm.valid
            && this.activityIdControl.value) {
            this.isActivityControlSetByQueryParam = true;
            reportConfiguredByQueryParams = true;
            this.onApplyButtonClick(false);
          }
          reportConfiguredByQueryParams = true;
          this.isActivityControlSetByQueryParam = true;
        }
      })
    );
  }

  private removeAllOptionalFields() {
    this.showActivityDropdown = false;
    this.showStudentDropdown = false;
    this.showGradeDropdown = false;
    this.showSkillDropdown = false;

    this.selectionForm.removeControl(this.formControlNames.studentId);
    this.selectionForm.removeControl(this.formControlNames.activityId);
    this.selectionForm.removeControl(this.formControlNames.grade);
    this.selectionForm.removeControl(this.formControlNames.skill);
  }

  /**
   * This registers value change subscriptions for **default** controls.
   *
   * Controls that are added dynamically should have their valueChange subscriptions handled when adding the control.
   */
  private initDefaultControlSubscriptions(): void {
    // Handles changing the report selection, which resets the form controls.
    this.subscriptions.add(
      this.reportControl.valueChanges.pipe(
        distinctUntilChanged(),
      ).subscribe((reportQueryParam: ProgressReportQueryParamByType) => {
        this.resetToOriginal();
        this.configureButtonStates();
        if (reportQueryParam) {
          this.classroomIdControl.setValue(null);
          this.classroomIdControl.enable();
          this.removeAllOptionalFields();
          // When changing reports and there is still a selected classroom, set up the rest of fields by report type
          this.setupFieldsByReportType(reportQueryParam);
        } else {
          this.removeAllOptionalFields();
          this.classroomIdControl.disable();
        }
        this.titleService.setTitle(`Reports | Progress Reports | ${this.getReportTitle(reportQueryParam)} | ${this.appConfigService.getBrandTitle}`);
      }),
    );

    // Handles changing the classroom id, which loads products.
    this.subscriptions.add(
      combineLatest([this.classroomIdControl.valueChanges, this.reportControl.valueChanges]).pipe(
        tap(([classroomId]) => {
          this.selectedClassroom = this.findClassroomById(classroomId);
          this.learningFacade.selectClassroom(this.selectedClassroom);
        }),
        distinctUntilChanged((prev, current) => _.isEqual(prev, current)),
        tap(([classroomId]) => {
          this.resetViewGroup();
          this.resetFilterOptions();
          const classGroupOptions = this.selectedClassroom?.classroomGroups?.map(group => ({
            label: group.groupName,
            value: group.classroomStudentGroupId,
          })) ?? [];
          this.viewGroupOptions = this.viewGroupOptions.concat(classGroupOptions);
          if (classroomId) {
            this.productSkuIdControl.enable();
            this.isLoadingProducts = true;
          } else {
            this.productSkuIdControl.disable();
            this.isLoadingProducts = false;
          }
          this.studentIdControl?.setValue(null);
        }),
        switchMap(() => this.learningFacade.classroomLicenses$)
      ).subscribe((products: SchoolLicense[]) => {
        if (products) {
          let filteredProducts = products;

          if (this.reportControl.value === ProgressReportQueryParamByType.ClassSkillsHeatmap) {
            filteredProducts = BrandedReportsHelpers
              .getProductsWithCapabilities(filteredProducts, Capabilities.HeatmapReport);
          } else if (this.reportControl.value === ProgressReportQueryParamByType.StandardsDetail) {
            filteredProducts = BrandedReportsHelpers
              .getProductsWithCapabilities(filteredProducts, Capabilities.StandardsDetailReport);
          } else if (this.reportControl.value === ProgressReportQueryParamByType.StandardsOverTime) {
            filteredProducts = BrandedReportsHelpers
              .getProductsWithCapabilities(filteredProducts, Capabilities.StandardsOverTimeReport);
          } else if (this.reportControl.value === ProgressReportQueryParamByType.SkillAverages) {
            filteredProducts = BrandedReportsHelpers
              .getProductsWithCapabilities(filteredProducts, Capabilities.SkillsAverageReport);
          } else {
            filteredProducts = BrandedReportsHelpers
              .getProductsWithCapabilities(filteredProducts, Capabilities.ProgressReports);
          }

          this.products = filteredProducts;
          this.products$.next(products);
          const defaultValue = this.classroomIdControl.value ? this.getDefaultSkuId() : null;
          this.productSkuIdControl.setValue(defaultValue);
          this.setupFieldsByReportType(this.reportControl?.value);
          this.isLoadingProducts = false;
        }
      }),
    );

    // Handles updates to available classrooms.
    this.subscriptions.add(
      combineLatest([this._learningFacadeClassrooms$, this.reportControl.valueChanges]).pipe(
        skipWhile(([classes]) => classes === null)
      ).subscribe(([classrooms]) => {
        let applicableClassrooms = BrandedReportsHelpers.getClassroomsWithCapabilities(
          classrooms,
          Capabilities.ProgressReports
        );

        if (this.reportControl.value === ProgressReportQueryParamByType.ClassSkillsHeatmap) {
          applicableClassrooms = BrandedReportsHelpers.getClassroomsWithCapabilities(
            applicableClassrooms,
            Capabilities.HeatmapReport
          );
        }

        if (this.reportControl.value === ProgressReportQueryParamByType.StandardsDetail) {
          applicableClassrooms = BrandedReportsHelpers.getClassroomsWithCapabilities(
            applicableClassrooms,
            Capabilities.StandardsDetailReport
          );
        }

        if (this.reportControl.value === ProgressReportQueryParamByType.StandardsOverTime) {
          applicableClassrooms = BrandedReportsHelpers.getClassroomsWithCapabilities(
            applicableClassrooms,
            Capabilities.StandardsOverTimeReport
          );
        }

        if (this.reportControl.value === ProgressReportQueryParamByType.SkillAverages) {
          applicableClassrooms = BrandedReportsHelpers.getClassroomsWithCapabilities(
            applicableClassrooms,
            Capabilities.SkillsAverageReport
          );
        }

        const mutatableClassrooms = [...applicableClassrooms];
        this.classrooms = mutatableClassrooms.sort((a, b) => a?.name?.localeCompare(b.name));
        const defaultValue = this.getDefaultClassroomId();
        this.classroomIdControl.setValue(defaultValue);
        this.isLoadingClassrooms = false;
      }),
    );

    // Activities are dependent on Product - handles Activity Dropdown on Product change
    this.subscriptions.add(
      this.productSkuIdControl.valueChanges.pipe(
        tap((productId) => {
          this.selectedProduct = this.findProductById(productId);
        }),
        distinctUntilChanged(),
        map((productId) => {
          // Update dynamic controls for reports using activities when changing products
          this.topicsList$.next([]);
          this.activityIdControl.setValue(null);
          if (!this.activityReportTypes.includes(this.reportControl.value)) {
            this.selectionForm.removeControl(this.formControlNames.activityId);
          }
          if (this.showObjectiveFilter) {
            this.resetFilterOptions();
          }
          return productId;
        }),
        filter(productId => this.activityReportTypes.includes(this.reportControl.value) && !!productId),
        skipWhile(() => this.products?.length < 1),
        mergeMap(productId => this.loadActivitiesForProduct(productId)),
        tap(() => {
          this.isLoadingActivity = false;
          this.activityIdControl.enable();
        })
      ).subscribe(),
    );

    this.subscriptions.add(
      this.viewGroupControl.valueChanges.pipe(
        distinctUntilChanged(),
        map(() => {
          if (this.showObjectiveFilter) {
            this.resetFilterOptions();
          }
        })
      ).subscribe(),
    );
  }

  // Dynamically get the report title
  private getReportTitle(reportType: ProgressReportQueryParamByType): string {
    if (reportType) {
      return getReportTypeFromQueryParam(reportType).toString();
    }

    return this.defaultDropDownText;
  }

  /**
   * Setup extra fields based on selected report type.
   */
  initDynamicFieldsByReportType() {
    this.setupFieldsByReportType(this.reportControl.value);
  }

  /**
   * Setup controls for report by type.
   *
   * Can be called when dependent-controls change the data or dropdowns available.
   */
  private setupFieldsByReportType(reportQueryParam: ProgressReportQueryParamByType) {
    // unset flags before letting report type determine which get re-enabled
    this.showStudentDropdown = false;
    this.showGradeDropdown = false;
    this.showSkillDropdown = false;
    this.showViewGroupDropdown = false;
    this.showActivityDropdown = false;
    this.showObjectiveFilter = false;

    if (!reportQueryParam) {
      // no report type selected
      return;
    }

    // reports with activity fields
    if (this.activityReportTypes.includes(reportQueryParam)) {
      this.showActivityDropdown = true;
      this.setupActivityField();
    }

    // report specific setup
    switch (reportQueryParam) {
    case ProgressReportQueryParamByType.IndividualStudentOverTime:
      this.setupStudentOverTimeFields();
      break;
    case ProgressReportQueryParamByType.ClassSpread:
      this.showViewGroupDropdown = true;
      break;
    case ProgressReportQueryParamByType.ClassSkillsHeatmap:
      this.showViewGroupDropdown = true;
      break;
    case ProgressReportQueryParamByType.StandardsDetail:
      this.showGradeDropdown = true;
      break;
    case ProgressReportQueryParamByType.StandardsOverTime:
      this.showGradeDropdown = true;
      this.showSkillDropdown = true;
      break;
    case ProgressReportQueryParamByType.ClassOverTime:
      this.showViewGroupDropdown = true;
      this.showObjectiveFilter = true;
      this.resetFilterOptions();
      break;
    case ProgressReportQueryParamByType.SkillAverages:
      this.showGradeDropdown = true;
      this.showSkillDropdown = true;
      break;
    default:
      // we should handle all cases explicitly, so warn if we miss one
      console.warn('Report type may not be set up properly', reportQueryParam);
      break;
    }
  }

  private setupStudentOverTimeFields() {
    const classroomId = this.classroomIdControl.value;
    this.isLoadingStudent = true;

    this.showStudentDropdown = this.reportControl.value === ProgressReportQueryParamByType.IndividualStudentOverTime;
    this.students = [];

    if (!classroomId) {
      this.studentIdControl.setValue(null);
      this.isLoadingStudent = false;
      this.students = [];

      return;
    }

    const matchingClassroom = this.classrooms.find(classroom => classroom.classroomId === classroomId);

    if (matchingClassroom) {
      const mutableStudents = [...matchingClassroom.students];

      const sortedStudents = mutableStudents.sort((a, b) => a?.lastName?.localeCompare(b.lastName));
      this.students = sortedStudents
        .map((student) => {
          const updatedStudent = {
            ...student,
            displayName: `${student.lastName}, ${student.firstName}`
          };

          this.selectionForm.setControl(this.formControlNames.studentId, this.studentIdControl);

          return updatedStudent;
        });
    } else {
      this.selectionForm.setControl(this.formControlNames.studentId, this.studentIdControl);
      this.studentIdControl.setValue(null);
    }

    this.selectionForm.setControl(this.formControlNames.studentId, this.studentIdControl);
    this.isLoadingStudent = false;
  }

  private setupActivityField() {
    this.topicsList$.next([]);
    this.activityIdControl.disable();
    this.selectionForm.setControl(this.formControlNames.activityId, this.activityIdControl);

    if (this.productSkuIdControl.value && this.classroomIdControl.value) {
      this.subscriptions.add(
        this.loadActivitiesForProduct(this.productSkuIdControl.value).subscribe(() => {
          this.isLoadingActivity = false;
          this.activityIdControl.enable();
        }),
      );
    }
  }

  /**
   * Form controls should be bound to query params so they are set when page loads from link
   */
  private initQueryParamBindings(): void {
    // BIND report form control to query parameters.
    this.subscriptions.add(
      this.queryParamService.twoWayBindFormControlWithMapping({
        queryParamName: 'report',
        control: this.reportControl,
        mapQueryToControl: this.setReportControl.bind(this),
        mapControlToQuery: this.setReportQueryParam.bind(this)
      }),
    );

    // BIND classroom form control to query parameters.
    this.subscriptions.add(
      this.queryParamService.twoWayBindFormControl({
        queryParamName: 'classroomId',
        control: this.classroomIdControl,
      })
    );

    // BIND product form control to query parameters.
    this.subscriptions.add(
      this.queryParamService.twoWayBindFormControl({
        queryParamName: 'skuId',
        control: this.productSkuIdControl,
        callBackAfterSetControl: this.applyClassOverTimeSelections.bind(this)
      })
    );

    // BIND student form control to query params
    this.subscriptions.add(
      this.queryParamService.twoWayBindFormControl({
        queryParamName: 'student',
        control: this.studentIdControl,
        callBackAfterSetControl: this.applyStudentOverTimeSelections.bind(this)
      })
    );

    this.subscriptions.add(
      this.queryParamService.twoWayBindFormControl({
        queryParamName: 'activity',
        control: this.activityIdControl,
        callBackAfterSetControl: this.applyClassSpreadSelections.bind(this),
      })
    );

    this.subscriptions.add(
      this.queryParamService.twoWayBindFormControl({
        queryParamName: 'skill',
        control: this.skillControl,
        callBackAfterSetControl: this.handleProductsSubscriptionLogic.bind(this),
      })
    );
  }

  setReportControl(queryParam: string) {
    const matchingReport = this.reportTypes?.find(report => report.queryParam === queryParam);
    this.reportControl?.setValue(matchingReport?.queryParam);
    return matchingReport?.queryParam;
  }

  setReportQueryParam(reportName: string) {
    const matchingReport = this.reportTypes?.find(report => report.queryParam === reportName);
    return matchingReport?.queryParam;
  }

  /**
   * Resets the form to only have default form controls.
   *
   * Not all reports have the same controls.
   */
  private resetToDefaultFormControls(): void {
    Object
      .keys(this.selectionForm.controls)
      .filter(controlName => !this.defaultControlNames.includes(controlName))
      .forEach((controlName) => {
        this.selectionForm.removeControl(controlName);
      });
  }

  private resetToOriginal(): void {
    this.resetToDefaultFormControls();
    this.resetPrinting();
    this.resetExporting();
    // Remove any dynamic menus shown on UI
    this.showStudentDropdown = false;
    this.showGradeDropdown = false;
    this.showSkillDropdown = false;
    if (this.showObjectiveFilter) {
      this.resetFilterOptions();
    }
  }

  private resetPrinting(): void {
    this.isPrinting = false;
    this.enablePrint = false;
    this.printableReportHeadings = [];
    this.resetIsPrintingForReportConfigs();
  }

  private resetIsPrintingForReportConfigs() {
    this.reportTypes.forEach((reportType, index) => {
      this.reportTypes[index].isPrinting = false;
    });
  }

  private resetExporting(): void {
    this.isExporting = false;
    this.enableExport = false;
    if (this.exportDownload) {
      this.exportDownload.reset();
    }
    this.resetIsExportingForReportConfigs();
  }

  private resetIsExportingForReportConfigs() {
    this.reportTypes.forEach((reportType, index) => {
      this.reportTypes[index].isExporting = false;
    });
  }

  private findProductById(skuId?: string): SchoolLicense | undefined {
    return BrandedReportsHelpers.findProductById(this.products, skuId);
  }

  private findClassroomById(classroomId?: string): Classroom | undefined {
    return BrandedReportsHelpers.findClassroomById(this.classrooms, classroomId);
  }

  private findReportModelByType(report: ProgressReportQueryParamByType): ProgressReportTypeModel | undefined {
    return this.reportTypes
      .find((reportType: ProgressReportTypeModel) => reportType.queryParam === report);
  }

  private findStudentNameById(studentId: string): string {
    return this.students.find(student => student.userId === studentId).displayName;
  }

  private getDefaultClassroomId(): null | string {
    return BrandedReportsHelpers.getDefaultClassroomId(this.classrooms, this.classroomIdControl);
  }

  private getDefaultSkuId(): null | string {
    return BrandedReportsHelpers.getDefaultSkuId(this.products, this.productSkuIdControl);
  }

  private loadProductsForClassroom(classroomId: string): Observable<SchoolLicense[]> {
    const classroom = this.findClassroomById(classroomId);
    if (classroom) {
      this.isLoadingProducts = true;
      this.productSkuIdControl.disable();
      return this.classroomService.getClassroomProductLicenses(
        classroom.educationalUnitId,
        classroom.classroomId,
      ).pipe(
        map((res) => {
          if (res?.success) {
            return res.response.filter(p => p.classroomCapabilities?.includes(Capabilities.ProgressReports));
          }
          throw new Error(res?.messages?.[0] ?? 'Unable to load products');
        }),
        finalize(() => {
          this.isLoadingProducts = false;
          this.productSkuIdControl.enable();
        })
      );
    }
    // no classroom selected, can't load products
    return of([]);
  }

  private loadActivitiesForProduct(productId?: string): Observable<Topic[]> {
    const currentProductId = productId;
    const currentProduct = this.products.find(product => product.skuId === currentProductId);
    const variantType = Helpers.mapVariantToGradeType(currentProduct?.variantType);
    this.isLoadingActivity = true;

    return this.productService.getTopicsByProductAndGrade(
      currentProduct?.productType,
      variantType,
      this.classroomIdControl.value,
    ).pipe(
      map((groups) => {
        if (groups?.length) {
          const topics: Topic[] = groups.reduce((ret, group) => (
            ret.concat(group.items.filter(t => t.topicType === TopicType.Assessment
              || t.topicType === TopicType.Pretest
              || t.topicType === TopicType.Posttest))
          ), []);
          let renamedTopics: Topic[] = [];

          if (Helpers.hasProductCapability(
            currentProduct,
            Capabilities.AddUnitToTopicName,
            CapabilityType.Classroom)) {
            let renamedTopic: Topic;
            topics.forEach((topic) => {
              renamedTopic = _.cloneDeep(topic);
              renamedTopic.topicName = `Unit ${topic.unit} ${topic.topicName}`;
              renamedTopics.push(renamedTopic);
            });
          } else {
            renamedTopics = topics;
          }
          this.topicsList$.next(renamedTopics);
          return renamedTopics;
        }
        return [];
      }),
    );
  }

  resetViewGroup() {
    this.viewGroupOptions = [this.VIEW_WHOLE_CLASS_OPTION];
    this.clearViewGroupControl();
  }

  clearViewGroupControl() {
    setTimeout(() => this.viewGroupControl.reset(
      this.VIEW_WHOLE_CLASS_OPTION.value,
      { emitEvent: true }
    ), 0);
  }

  resetFilterOptions(): void {
    if (this.showObjectiveFilter) {
      this.objectiveFilterOptions = [this.VIEW_TOTAL_SCORE_OPTION];
      this.objectiveFilterInitialized = false;
      this.clearObjectiveFilterControl();
    }
  }

  clearObjectiveFilterControl() {
    setTimeout(() => this.objectiveFilterControl.reset(
      this.VIEW_TOTAL_SCORE_OPTION.value,
      { emitEvent: true }
    ), 0);
  }

  conditionallyAppendViewGroupParam<T extends { classroomStudentGroupId?: string }>(params: T): void {
    if (this.viewGroupControl.value !== this.VIEW_WHOLE_CLASS_OPTION.value) {
      // eslint-disable-next-line no-param-reassign
      params.classroomStudentGroupId = this.viewGroupControl.value;
    }
  }

  conditionallyAppendObjectiveFilter<T extends { objective?: string }>(params: T): void {
    if (!Helpers.isNullOrUndefined(this.objectiveFilterControl.value)
      && this.objectiveFilterControl.value !== this.VIEW_TOTAL_SCORE_OPTION.value) {
      // eslint-disable-next-line no-param-reassign
      params.objective = this.objectiveFilterControl.value;
    }
  }

  printAllScoreDetails(): void {
    switch (this.reportControl.value) {
    case ProgressReportQueryParamByType.ClassSpread:
      this.printClassSpreadScoreDetails();
      break;
    default:
      // unsupported report type
      break;
    }
  }

  private get classSpreadHasScoreDetails(): boolean {
    return !!this.classSpreadReportData?.benchmarkResults
      ?.some(result => this.classSpreadResultHasPrintableScoreDetails(result));
  }

  private printClassSpreadScoreDetails() {
    if (!this.classSpreadHasScoreDetails) {
      return;
    }

    // Filter to only students with score details
    const studentsWithDetails = this.classSpreadReportData.benchmarkResults
      .filter(result => this.classSpreadResultHasPrintableScoreDetails(result))
      .map(result => ({
        studentName: `${result.firstName} ${result.lastName}`,
        score: result.score,
        benchmark: result.benchmark,
        parts: result.scoreDetail as any,
      }));

    if (studentsWithDetails.length === 0) {
      return;
    }
    const currentProduct = this.products.find(product => product.skuId === this.productSkuIdControl.value);

    const params: IPrintAllScoreDetailsParams = {
      className: this.classrooms.find(classroom => classroom.classroomId === this.classroomIdControl.value)?.name,
      productName: currentProduct?.description,
      activityName: this.topicsList$.value.find(topic => topic.topicKey === this.activityIdControl.value)?.topicName,
      topicType: TopicType.Assessment,
      productType: currentProduct.productType,
      students: studentsWithDetails,
    };

    ScoreDetailPrintAllModalComponent.open(this.modalService, params);
  }

  /**
   * Result should have entries in the score detail array and not be in the "Add Score" status
   * to be included in the "Print All Score Details" modal.
   */
  private classSpreadResultHasPrintableScoreDetails(result: ClassSpreadBenchmarkResult) {
    return Array.isArray(result.scoreDetail)
      && (result.scoreDetail.length > 0 && result.assessmentStatus !== AssessmentStatus.AddScore);
  }


  /**
   * Announces the status of the results, including the number of results found and the number
   * of active filters, for screen readers.
   */
  private announceResultsStatus(resultCount: number): void {

    let resultsMessage: string;

    switch (resultCount) {
    case 0:
      resultsMessage = 'No results found.';
      break;
    case this.heatmapReportType:
      resultsMessage = 'Heatmap Loaded';
      break;
    default:
      resultsMessage = `${resultCount} ${resultCount === 1 ? 'result' : 'results'} found.`;
      break;
    }
    if (this.statusMessage === resultsMessage) {
      this.statusMessage = '';
    }
    setTimeout(() => {
      this.statusMessage = resultsMessage;
    }, 100);
  }

}
